import type { ReactNode } from 'react';
import {
  BACK_HAND,
  BACK_HAND_FILL,
  BODY_FILL,
  BODY_OUTLINE,
  BOOK_COVER,
  BOOK_FRONT,
  BOOK_PAGES,
  BROW,
  CHEEK,
  DIVIDE,
  EQUALS,
  EYE_LEFT,
  EYE_RIGHT,
  FRONT_HAND_FILL,
  FRONT_HAND_LINE,
  GLASSES,
  MOUTH,
  PLUS_BLUE,
  PLUS_ORANGE,
  SHINE as SHINE_SPOTS,
  TONGUE,
} from './mascot-paths';
import { cx } from './styles';

export type MascotMood = 'idle' | 'happy' | 'thinking' | 'proud' | 'kind' | 'sleepy';
export type MascotPose =
  | 'idle'
  | 'wave'
  | 'celebrate'
  | 'read'
  | 'study'
  | 'count'
  | 'write'
  | 'diploma'
  | 'star'
  | 'think'
  | 'sleep';

const sizes = {
  fit: '',
  xs: 'h-12',
  sm: 'h-20',
  md: 'h-24',
  lg: 'h-32',
  xl: 'h-40',
  '2xl': 'h-52',
};

const moodForPose: Record<MascotPose, MascotMood> = {
  idle: 'idle',
  wave: 'happy',
  celebrate: 'proud',
  read: 'happy',
  study: 'happy',
  count: 'happy',
  write: 'idle',
  diploma: 'proud',
  star: 'proud',
  think: 'thinking',
  sleep: 'sleepy',
};

const LINE = '#529a43';
const BODY = '#86bf3e';
const SHINE = '#acc84e';
const INK = '#123f1e';
const TONGUE_RED = '#de5926';
const ORANGE = '#f28c00';
const ROUNDED = 'Andika, ui-rounded, system-ui, sans-serif';
const pivot = '[transform-box:fill-box]';

function Body() {
  return (
    <g>
      <path d={BODY_OUTLINE} fill={LINE} />
      <path d={BODY_FILL} fill={BODY} fillRule="evenodd" />
    </g>
  );
}

function Arm({ d }: { readonly d: string }) {
  return (
    <g fill="none" strokeLinecap="round">
      <path d={d} stroke={LINE} strokeWidth="8.4" />
      <path d={d} stroke={BODY} strokeWidth="5.2" />
    </g>
  );
}

function Hand({ x, y, r = 5 }: { readonly x: number; readonly y: number; readonly r?: number }) {
  return <circle cx={x} cy={y} r={r} fill={BODY} stroke={LINE} strokeWidth="1.6" />;
}

function Eyes({ mood }: { readonly mood: MascotMood }) {
  if (mood === 'proud' || mood === 'sleepy') {
    const arc = mood === 'proud' ? 'q21 -24 42 0' : 'q21 20 42 0';
    const y = mood === 'proud' ? 318 : 304;
    return (
      <path
        d={`M305 ${String(y)} ${arc} M429 ${String(y)} ${arc}`}
        stroke={INK}
        strokeWidth="11"
        fill="none"
        strokeLinecap="round"
      />
    );
  }
  return (
    <g transform={mood === 'thinking' ? 'translate(8 -10)' : undefined}>
      <g className={cx(pivot, 'origin-center motion-safe:animate-blink')}>
        <path d={EYE_LEFT} fill={INK} />
        <path d={EYE_RIGHT} fill={INK} />
        <path d={SHINE_SPOTS} fill="#fff" />
      </g>
    </g>
  );
}

function Brows({ mood }: { readonly mood: MascotMood }) {
  return (
    <g>
      <path d={BROW} fill={INK} />
      {mood === 'thinking' ? (
        <path
          d="M428 232 q20 -12 40 -3"
          stroke={INK}
          strokeWidth="11"
          fill="none"
          strokeLinecap="round"
        />
      ) : null}
    </g>
  );
}

function Mouth({ mood }: { readonly mood: MascotMood }) {
  switch (mood) {
    case 'thinking':
      return (
        <path
          d="M342 392 q15 -11 30 0 t30 0"
          stroke={INK}
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
        />
      );
    case 'sleepy':
      return <ellipse cx="372" cy="392" rx="14" ry="11" fill={INK} />;
    case 'kind':
      return (
        <path
          d="M336 368 C352 410 408 410 430 362"
          stroke={INK}
          strokeWidth="11"
          fill="none"
          strokeLinecap="round"
        />
      );
    case 'happy':
    case 'proud':
    case 'idle':
      return (
        <g>
          <path d={MOUTH} fill={INK} />
          <path d={TONGUE} fill={TONGUE_RED} />
        </g>
      );
  }
}

function Face({ mood }: { readonly mood: MascotMood }) {
  return (
    <g>
      <path d={CHEEK} fill={SHINE} />
      <Eyes mood={mood} />
      <Brows mood={mood} />
      <Mouth mood={mood} />
      <path d={GLASSES} fill={ORANGE} fillRule="evenodd" />
    </g>
  );
}

const signs = (
  <g>
    <path d={PLUS_BLUE} fill="#2a7bb1" />
    <path d={PLUS_ORANGE} fill="#f28621" />
    <path d={EQUALS} fill="#fac614" fillRule="evenodd" />
    <path d={DIVIDE} fill="#de5926" fillRule="evenodd" />
  </g>
);

const book = (
  <g>
    <path d={BOOK_COVER} fill="#28387a" />
    <path d={BOOK_FRONT} fill="#3e4fb4" fillRule="evenodd" />
    <path d={BOOK_PAGES} fill="#f2e3b4" fillRule="evenodd" />
    <path d={FRONT_HAND_FILL} fill={BODY} fillRule="evenodd" />
    <path d={FRONT_HAND_LINE} fill={LINE} fillRule="evenodd" />
    <path d={BACK_HAND} fill={LINE} />
    <path d={BACK_HAND_FILL} fill={BODY} fillRule="evenodd" />
  </g>
);

const block = (
  <g strokeLinejoin="round">
    <Arm d="M60 91 Q66 101 76 100" />
    <path d="M77 86 L90 79 L116 83 L103 90 Z" fill="#a483ea" stroke="#3b2477" strokeWidth="1.6" />
    <path
      d="M103 90 L116 83 L116 108 L103 116 Z"
      fill="#5a36b3"
      stroke="#3b2477"
      strokeWidth="1.6"
    />
    <path d="M77 86 L103 90 L103 116 L77 112 Z" fill="#7e57d6" stroke="#3b2477" strokeWidth="1.6" />
    <text
      x="90"
      y="109"
      textAnchor="middle"
      fontSize="21"
      fontWeight="700"
      fontFamily={ROUNDED}
      fill="#ffd54a"
    >
      3
    </text>
    <Hand x={77} y={100} />
    <Hand x={115.5} y={97} />
  </g>
);

const pencil = (
  <g strokeLinejoin="round">
    <Arm d="M60 91 Q66 101 76 101" />
    <g transform="rotate(-40 95 97)">
      <rect
        x="72"
        y="90"
        width="38"
        height="13"
        rx="2.5"
        fill="#ffc83d"
        stroke="#7a4f00"
        strokeWidth="1.5"
      />
      <path d="M72 96.5 h38" stroke="#f0a800" strokeWidth="2" />
      <rect
        x="66"
        y="90"
        width="8"
        height="13"
        rx="2.5"
        fill="#ff8fa3"
        stroke="#7a4f00"
        strokeWidth="1.5"
      />
      <path d="M110 90 L122 96.5 L110 103 Z" fill="#f6d7a7" stroke="#7a4f00" strokeWidth="1.5" />
      <path d="M118 94.4 L122 96.5 L118 98.6 Z" fill="#3b2a17" />
    </g>
    <Hand x={76} y={101} />
    <Hand x={101.5} y={99} />
  </g>
);

const diploma = (
  <g strokeLinejoin="round">
    <Arm d="M60 91 Q66 101 74 101" />
    <rect
      x="74"
      y="84"
      width="40"
      height="25"
      rx="3"
      fill="#fff5d9"
      stroke="#7a4f00"
      strokeWidth="1.5"
    />
    <rect
      x="71"
      y="82"
      width="6"
      height="29"
      rx="3"
      fill="#ffe7a8"
      stroke="#7a4f00"
      strokeWidth="1.5"
    />
    <rect
      x="111"
      y="82"
      width="6"
      height="29"
      rx="3"
      fill="#ffe7a8"
      stroke="#7a4f00"
      strokeWidth="1.5"
    />
    <path d="M82 91 h22 M82 97 h15" stroke="#e0b25a" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M100 103 l-4 11 l5 -2.5 l3.5 4 l1.5 -11.5 Z"
      fill="#e53935"
      stroke="#8a1c1c"
      strokeWidth="1.4"
    />
    <circle cx="102.5" cy="102.5" r="6" fill="#ffc83d" stroke="#b77900" strokeWidth="1.2" />
    <Hand x={73.5} y={101} />
    <Hand x={115} y={99} />
  </g>
);

const star = (
  <g strokeLinejoin="round">
    <Arm d="M60 91 Q66 101 76 99" />
    <path
      d="M96 72 L101.6 84.2 L115 85.7 L105 94.6 L107.8 107.8 L96 101 L84.2 107.8 L87 94.6 L77 85.7 L90.4 84.2 Z"
      fill="#ffc83d"
      stroke="#a86b00"
      strokeWidth="1.7"
    />
    <path d="M90 87.5 l2.4 -3.6" stroke="#fff5cc" strokeWidth="1.6" strokeLinecap="round" />
    <Hand x={78} y={98} />
    <Hand x={113} y={95} />
  </g>
);

const FINGERS = [
  'M-2.6 -3.4 L-5 -11',
  'M0.4 -4 L0.9 -12.4',
  'M3.2 -3.2 L6.2 -10.2',
  'M-4 1 L-9.4 -1.6',
];

const UPPER_ARM = 'M80 97.5 Q93 100 104.5 96.5';
const FOREARM = 'M104.5 96.5 Q109.5 89 111 80.5';

type Layer = 'outline' | 'fill';

function OpenHand({ layer }: { readonly layer: Layer }) {
  const outline = layer === 'outline';
  return (
    <g strokeLinecap="round">
      <g stroke={outline ? LINE : BODY} strokeWidth={outline ? 5.8 : 3.3}>
        {FINGERS.map((finger) => (
          <path key={finger} d={finger} />
        ))}
      </g>
      <ellipse
        cx="0"
        cy="-0.6"
        rx={outline ? 6.9 : 5.3}
        ry={outline ? 6.3 : 4.7}
        fill={outline ? LINE : BODY}
      />
    </g>
  );
}

function Forearm({ layer, waving }: { readonly layer: Layer; readonly waving: boolean }) {
  const outline = layer === 'outline';
  return (
    <g className={cx(pivot, 'origin-center', waving ? 'motion-safe:animate-wave' : null)}>
      <rect x="64.5" y="56.5" width="80" height="80" fill="none" />
      <path d={FOREARM} stroke={outline ? LINE : BODY} strokeWidth={outline ? 8.4 : 5.2} />
      <g className={cx(pivot, 'origin-center', waving ? 'motion-safe:animate-wrist' : null)}>
        <rect x="91" y="60.5" width="40" height="40" fill="none" />
        <g transform="translate(111.6 74.1) rotate(12)">
          <OpenHand layer={layer} />
        </g>
      </g>
    </g>
  );
}

function RaisedArm({ waving }: { readonly waving: boolean }) {
  return (
    <g fill="none" strokeLinecap="round">
      <path d={UPPER_ARM} stroke={LINE} strokeWidth="8.4" />
      <Forearm layer="outline" waving={waving} />
      <path d={UPPER_ARM} stroke={BODY} strokeWidth="5.2" />
      <Forearm layer="fill" waving={waving} />
    </g>
  );
}

const confetti = (
  <g className={cx(pivot, 'origin-center motion-safe:animate-drift')}>
    <rect x="18" y="14" width="6" height="6" rx="1.2" fill="#29a3e0" transform="rotate(20 21 17)" />
    <rect
      x="104"
      y="30"
      width="5"
      height="8"
      rx="1.2"
      fill="#f43f5e"
      transform="rotate(-25 106 34)"
    />
    <circle cx="30" cy="30" r="3" fill="#ffc83d" />
    <rect x="37" y="4" width="5" height="8" rx="1.2" fill="#8b5cf6" transform="rotate(35 39 8)" />
    <circle cx="119" cy="24" r="2.6" fill="#f7941d" />
    <path
      d="M100 8 L102 13 L107 14 L103 17.5 L104 22.5 L100 20 L96 22.5 L97 17.5 L93 14 L98 13 Z"
      fill="#ffc83d"
      stroke="#a86b00"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </g>
);

const thought = (
  <g className={cx(pivot, 'motion-safe:animate-drift')}>
    <circle cx="104" cy="22" r="2.4" fill="#fff" stroke="#b9c3d0" strokeWidth="1.6" />
    <circle cx="110" cy="13" r="3.6" fill="#fff" stroke="#b9c3d0" strokeWidth="1.6" />
    <circle cx="117" cy="-1" r="10" fill="#fff" stroke="#b9c3d0" strokeWidth="1.6" />
    <text
      x="117"
      y="4.5"
      textAnchor="middle"
      fontSize="15"
      fontWeight="700"
      fontFamily={ROUNDED}
      fill="#1276d2"
    >
      ?
    </text>
  </g>
);

const snore = (
  <g
    className={cx(pivot, 'motion-safe:animate-drift')}
    fill="#1276d2"
    fontFamily={ROUNDED}
    fontWeight="700"
  >
    <text x="102" y="20" fontSize="14">
      z
    </text>
    <text x="111" y="9" fontSize="10" opacity="0.7">
      z
    </text>
  </g>
);

const LEGACY = 'translate(-40 -40) scale(5.6)';

function Prop({ pose }: { readonly pose: MascotPose }): ReactNode {
  switch (pose) {
    case 'celebrate':
      return <g transform="translate(0 14)">{confetti}</g>;
    case 'count':
      return block;
    case 'write':
      return pencil;
    case 'diploma':
      return diploma;
    case 'star':
      return star;
    case 'think':
      return <g transform="translate(-3 30)">{thought}</g>;
    case 'sleep':
      return <g transform="translate(5 18)">{snore}</g>;
    case 'wave':
    case 'read':
    case 'study':
    case 'idle':
      return null;
  }
}

function Extras({ pose }: { readonly pose: MascotPose }): ReactNode {
  if (pose === 'read') {
    return book;
  }
  if (pose === 'study') {
    return (
      <>
        {signs}
        {book}
      </>
    );
  }
  return (
    <g transform={LEGACY}>
      <Prop pose={pose} />
    </g>
  );
}

const WITHOUT_TAIL =
  'M0 0H800V800H269L269 512L267 500L261 482L252 462L242 446L230 436L214 422L198 404L188 384L180 360L175 330L175 290L0 290Z';

export function MascotBust({ clipId }: { readonly clipId: string }) {
  return (
    <g clipPath={`url(#${clipId})`}>
      <clipPath id={clipId}>
        <path d={WITHOUT_TAIL} />
      </clipPath>
      <Body />
      <path d={CHEEK} fill={SHINE} />
      <path d={EYE_LEFT} fill={INK} />
      <path d={EYE_RIGHT} fill={INK} />
      <path d={SHINE_SPOTS} fill="#fff" />
      <path d={BROW} fill={INK} />
      <path d={MOUTH} fill={INK} />
      <path d={TONGUE} fill={TONGUE_RED} />
      <path d={GLASSES} fill={ORANGE} fillRule="evenodd" />
    </g>
  );
}

interface MascotProps {
  readonly pose?: MascotPose;
  readonly mood?: MascotMood;
  readonly size?: keyof typeof sizes;
  readonly className?: string;
}

export function Mascot({ pose = 'idle', mood, size = 'md', className }: MascotProps) {
  const face = mood ?? moodForPose[pose];
  const bounce = pose === 'celebrate' || pose === 'star';
  const armUp = pose === 'wave' || pose === 'celebrate';
  return (
    <svg
      viewBox="40 40 620 650"
      aria-hidden="true"
      className={cx('aspect-62/65 w-auto shrink-0 overflow-visible', sizes[size], className)}
    >
      <g
        className={cx(
          pivot,
          'origin-bottom',
          bounce ? 'motion-safe:animate-hop' : 'motion-safe:animate-sway',
        )}
      >
        {armUp ? (
          <g transform={LEGACY}>
            <RaisedArm waving={pose === 'wave'} />
          </g>
        ) : null}
        <Body />
        <Face mood={face} />
        <Extras pose={pose} />
      </g>
    </svg>
  );
}
